/**
 * Utilities for filtering and normalizing Marketo form values for GTM analytics.
 *
 * Marketo's native forms2.min.js returns PascalCase keys (Email, Company, FirstName)
 * in its onSuccess callback, while our custom React forms use camelCase (email, company,
 * firstName). pickGtmFields handles both conventions when building GTM event payloads.
 */

/** PascalCase keys from Marketo native → camelCase keys used in our analytics. */
const FIELD_ALIASES: Record<string, string> = {
  FirstName: "firstName",
  LastName: "lastName",
  Email: "email",
  Company: "company",
  Phone: "phone",
  Title: "title",
  NumberOfEmployees: "numberOfEmployees",
  numEmployeesIntercom: "numberOfEmployees",
  Subscribecheckbox: "subscribecheckbox",
  SubscribeCheckbox: "subscribecheckbox",
};

/**
 * User-input fields worth tracking in GTM analytics events.
 * Everything else — UTM params, Marketo internals (_mkt_trk, formVid, checksum),
 * tracking cookies (GACLIENTID__c, bizibleCookie), and geo-enrichment fields —
 * is noise that pollutes the dataLayer without downstream consumption.
 */
const GTM_FIELD_ALLOWLIST = new Set([
  "email",
  "firstName",
  "lastName",
  "company",
  "title",
  "phone",
  "numberOfEmployees",
  "subscribecheckbox",
]);

/**
 * Picks only user-input fields for GTM analytics events.
 * Normalizes PascalCase keys and filters to the allowlist.
 * Omits empty strings and nullish values to keep the payload clean.
 */
export function pickGtmFields(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const filtered: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    const normalizedKey = FIELD_ALIASES[key] ?? key;
    if (
      GTM_FIELD_ALLOWLIST.has(normalizedKey) &&
      value !== "" &&
      value != null
    ) {
      filtered[normalizedKey] = value;
    }
  }

  return filtered;
}
